<template>
    <div>
        <b-row>
            <b-col cols="12" class="d-flex align-items-center justify-content-between mb-1">
                <h5 class="mb-0">Stock por almacén</h5>
                <h5 class="mb-0">
                    <b-badge variant="light-primary" style="font-size: 1rem;">
                        TOTAL: {{ total }}
                    </b-badge>
                </h5>
            </b-col>

            <b-col cols="12">
                <b-alert variant="primary" show>
                    <div class="alert-body">
                        El stock total del producto es la <strong>suma de los almacenes</strong>: no se
                        edita directamente, sube o baja según lo que cargues aquí.
                        <template v-if="hojas.length">
                            Como este producto tiene variantes, el stock se lleva
                            <strong>por variante y almacén</strong>.
                        </template>
                    </div>
                </b-alert>
            </b-col>

            <b-col cols="12" v-if="loading">
                <div class="text-center py-2">
                    <b-spinner small class="mr-50" />
                    Cargando almacenes ...
                </div>
            </b-col>

            <b-col cols="12" v-else-if="!warehouses.length">
                <b-alert variant="warning" show>
                    <div class="alert-body">
                        No hay almacenes activos. Crea uno en
                        <b-link :to="{ name: 'warehouses' }" class="alert-link">Almacenes</b-link>
                        antes de cargar stock.
                    </div>
                </b-alert>
            </b-col>

            <b-col cols="12" v-else>
                <b-table-simple responsive striped class="mb-0">
                    <b-thead>
                        <b-tr>
                            <b-th style="width: 26%;">ALMACÉN</b-th>
                            <b-th style="width: 130px;" class="text-center">DISPONIBLE AQUÍ</b-th>
                            <b-th v-if="hojas.length" style="width: 24%;">VARIANTE</b-th>
                            <b-th style="width: 110px;">STOCK</b-th>
                            <b-th style="width: 275px;">MÍNIMO PARA NOTIFICAR</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="fila in filas" :key="fila.clave">

                            <!-- El almacén solo se rotula en la primera fila de su grupo. -->
                            <b-td>
                                <template v-if="fila.primera">
                                    <div class="font-weight-bolder">
                                        {{ fila.warehouse.alias }}
                                        <b-badge v-if="fila.warehouse.is_default" variant="light-primary" class="ml-50">
                                            POR DEFECTO
                                        </b-badge>
                                    </div>
                                    <small class="text-muted">{{ fila.warehouse.address }}</small>
                                </template>
                            </b-td>

                            <!-- El interruptor es del almacén completo, no de cada variante:
                                 por eso solo aparece en la primera fila del grupo. -->
                            <b-td class="text-center">
                                <b-form-checkbox
                                    v-if="fila.primera"
                                    :id="`available-${fila.warehouse._id}`"
                                    :checked="isEnabled(fila.warehouse._id)"
                                    switch
                                    class="d-inline-block interruptor-almacen"
                                    @change="toggleWarehouse(fila.warehouse._id, $event)"
                                >
                                    {{ isEnabled(fila.warehouse._id) ? 'Sí' : 'No' }}
                                </b-form-checkbox>
                            </b-td>

                            <b-td v-if="hojas.length">
                                <div>{{ fila.hoja.etiqueta }}</div>
                                <small class="text-muted">{{ fila.hoja.sku || 'sin SKU' }}</small>
                            </b-td>

                            <b-td>
                                <b-form-input
                                    :id="`qty-${fila.clave}`"
                                    class="stock-input"
                                    type="number"
                                    min="0"
                                    :value="celda(fila).qty"
                                    :disabled="!isEnabled(fila.warehouse._id)"
                                    :aria-label="`Stock de ${fila.hoja ? fila.hoja.etiqueta : 'producto'} en ${fila.warehouse.alias}`"
                                    @input="updateCelda(fila, 'qty', $event)"
                                />
                            </b-td>

                            <b-td>
                                <div class="d-flex align-items-center">
                                    <b-form-input
                                        :id="`min-${fila.clave}`"
                                        class="stock-input"
                                        type="number"
                                        min="0"
                                        :value="celda(fila).min_stock"
                                        :disabled="!isEnabled(fila.warehouse._id)"
                                        :aria-label="`Mínimo para notificar en ${fila.warehouse.alias}`"
                                        @input="updateCelda(fila, 'min_stock', $event)"
                                    />
                                    <small
                                        v-if="isEnabled(fila.warehouse._id)"
                                        class="ml-75 nota-alerta"
                                        :class="notificaEn(fila) ? 'text-success' : 'text-muted'"
                                    >{{ notificaEn(fila)
                                        ? `Notificar con ${notificaEn(fila)} o menos`
                                        : 'Sin notificar' }}</small>
                                </div>
                            </b-td>

                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <small class="text-muted d-block mt-50">
                    <strong>El mínimo sirve solo para notificar</strong>, no limita la venta.
                    Cada día a las 9:00 a. m. se revisa el inventario: si en un almacén la cantidad queda
                    <strong>igual o por debajo</strong> del mínimo, se envía un correo a los responsables
                    de ese almacén. Un mínimo en <strong>0 desactiva la notificación</strong> de ese almacén.
                </small>
            </b-col>
        </b-row>
    </div>
</template>

<script>

    import {
        BRow, BCol, BAlert, BBadge, BLink, BSpinner, BFormInput, BFormCheckbox,
        BTableSimple, BThead, BTbody, BTr, BTh, BTd
    } from 'bootstrap-vue';
    import axios from '@axios';

    export default {
        components: {
            BRow,
            BCol,
            BAlert,
            BBadge,
            BLink,
            BSpinner,
            BFormInput,
            BFormCheckbox,
            BTableSimple,
            BThead,
            BTbody,
            BTr,
            BTh,
            BTd
        },
        props: {
            value: {
                type: Array,
                default: () => []
            },
            /* Variantes del producto, para abrir el stock por hoja. */
            variants: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                warehouses: [],
                loading: true,
                /* Almacenes marcados como "disponible aquí". Se mantiene aparte del
                   valor para poder distinguir "0 unidades" de "no se vende aquí". */
                enabled: []
            }
        },
        computed: {
            total() {
                return this.value.reduce((sum, row) => sum + (parseInt(row.qty, 10) || 0), 0);
            },
            /* La hoja es la que lleva el precio: la subvariante si existe, si no
               la variante. Un producto sin variantes no tiene hojas. */
            hojas() {
                const salida = [];

                for (const variante of this.variants || []) {
                    const subs = variante.subvariants || variante.subVariants || [];

                    if (subs.length) {
                        subs.forEach(sub => salida.push({
                            id: String(sub.id),
                            etiqueta: `${variante.name} / ${sub.name}`,
                            sku: sub.sku
                        }));
                    } else {
                        salida.push({ id: String(variante.id), etiqueta: variante.name, sku: variante.sku });
                    }
                }

                return salida;
            },
            /* Una fila por almacén, o por almacén × hoja si hay variantes. */
            filas() {
                const salida = [];

                for (const warehouse of this.warehouses) {
                    if (!this.hojas.length) {
                        salida.push({
                            clave: String(warehouse._id),
                            warehouse,
                            hoja: null,
                            variant: null,
                            primera: true
                        });
                        continue;
                    }

                    this.hojas.forEach((hoja, i) => salida.push({
                        clave: warehouse._id + '|' + hoja.id,
                        warehouse,
                        hoja,
                        variant: hoja.id,
                        primera: i === 0
                    }));
                }

                return salida;
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(rows) {
                    /* Al hidratar desde la BD, todo almacén presente cuenta como habilitado. */
                    if (!this.enabled.length && rows && rows.length) {
                        this.enabled = rows.map(row => this.warehouseId(row))
                            .filter((id, i, todos) => todos.indexOf(id) === i);
                    }
                }
            }
        },
        beforeMount() {
            this.getWarehouses();
        },
        methods: {
            notificaEn(fila) {
                const minimo = Number(this.celda(fila).min_stock);

                return Number.isFinite(minimo) && minimo > 0 ? minimo : 0;
            },
            warehouseId(row) {
                if (!row || !row.warehouse) return null;
                /* El backend puede devolverlo poblado o como id suelto. */
                return String(row.warehouse._id ? row.warehouse._id : row.warehouse);
            },
            variantId(row) {
                return row && row.variant !== undefined && row.variant !== null ? String(row.variant) : null;
            },
            async getWarehouses() {
                try {
                    const response = await axios.get('/warehouses/select');
                    this.warehouses = response.data;
                } catch (e) {
                    this.warehouses = [];
                } finally {
                    this.loading = false;
                }
            },
            /* Valor guardado para esa celda (almacén + variante). */
            celda(fila) {
                const row = this.value.find(item =>
                    this.warehouseId(item) === String(fila.warehouse._id) &&
                    this.variantId(item) === fila.variant
                );

                return row || { warehouse: fila.warehouse._id, variant: fila.variant, qty: 0, min_stock: 0 };
            },
            isEnabled(warehouseId) {
                return this.enabled.indexOf(String(warehouseId)) !== -1;
            },
            emit(rows) {
                this.$emit('input', rows);
            },
            toggleWarehouse(warehouseId, checked) {
                const id = String(warehouseId);

                if (checked) {
                    this.enabled = this.enabled.concat([id]);

                    /* Al habilitar se crean las celdas que falten: una por hoja. */
                    const nuevas = this.filas
                        .filter(fila => String(fila.warehouse._id) === id)
                        .filter(fila => !this.value.find(item =>
                            this.warehouseId(item) === id && this.variantId(item) === fila.variant))
                        .map(fila => ({ warehouse: id, variant: fila.variant, qty: 0, min_stock: 0 }));

                    if (nuevas.length) this.emit(this.value.concat(nuevas));
                } else {
                    this.enabled = this.enabled.filter(item => item !== id);
                    /* Se retira del reparto: el total baja en consecuencia. */
                    this.emit(this.value.filter(item => this.warehouseId(item) !== id));
                }
            },
            updateCelda(fila, campo, rawValue) {
                const id = String(fila.warehouse._id);
                const valor = Math.max(0, parseInt(rawValue, 10) || 0);

                const rows = this.value.map(item => Object.assign({}, item, {
                    warehouse: this.warehouseId(item),
                    variant: this.variantId(item)
                }));

                const existente = rows.find(item => item.warehouse === id && item.variant === fila.variant);

                if (existente) {
                    existente[campo] = valor;
                } else {
                    rows.push({ warehouse: id, variant: fila.variant, qty: 0, min_stock: 0, [campo]: valor });
                }

                this.emit(rows);
            }
        }
    }

</script>

<style lang="scss" scoped>
    /* El interruptor se centra en su columna, pero su propia etiqueta
       (Si / No) se lee alineada a la izquierda del control. */
    .interruptor-almacen {
        text-align: left;
    }

    /* Los dos campos numericos comparten ancho para que la columna de stock y
       la de minimo se lean alineadas, y quede sitio a la derecha para la nota. */
    .stock-input {
        width: 90px;
        flex: 0 0 90px;
    }

    /* La nota va a la derecha del campo y nunca se parte en dos lineas. */
    .nota-alerta {
        white-space: nowrap;
    }
</style>
